import { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './blog.css';
import { useAppSelector } from '../../redux/Store';
import { loggedInUserSelector } from '../../redux/slice/LoggedInUserSlice';
import {
  createBlog,
  updateBlog,
  uploadBlogImage,
  getBlogCategories
} from './blogUtils';
import QuoteToolbar from './QuoteToolbar';

const BlogEditor = ({
  show,
  handleClose,
  blogToEdit,
  blogs,
  onSave,
  onExited
}) => {
  const { loggedInUser } = useAppSelector(loggedInUserSelector);
  const quillRef = useRef(null);

  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    category_name: '',
    image_url: '',
    author_image_url: '',
    content: '',
    meta_title: '',
    meta_description: '',
    keywords: '',
    author_profile_link: '',
    author_bio: '',
    read_time: '',
    tldr_summary: ''
  });

  const [activeTab, setActiveTab] = useState('content');
  const [imageFile, setImageFile] = useState(null);
  const [authorImageFile, setAuthorImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const initializedRef = useRef(false);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const categoriesData = await getBlogCategories();
        setCategories(categoriesData?.map((cat) => cat.name || cat));
      } catch (err) {
        setCategories([
          'Tax Planning',
          'Investments',
          'Financial Advice',
          'Tax Law',
          'Business Tax'
        ]);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (blogToEdit) {
      setFormData({
        ...blogToEdit,
        category_name: blogToEdit.category.name,
        author_bio: blogToEdit.author.author_bio,
        author_profile_link: blogToEdit.author.author_profile_link,
        author_image_url:blogToEdit.author.author_image_url,
        id: blogToEdit.id
      });
      initializedRef.current = true;
    } else if (!initializedRef.current) {
      setFormData({
        slug: '',
        title: '',
        category_name: '',
        image_url: '',
        author_image_url: '',
        content: '',
        meta_title: '',
        meta_description: '',
        keywords: '',
        author_profile_link: '',
        author_bio: '',
        read_time: '',
        tldr_summary: ''
      });
    }
  }, [blogToEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleEditorChange = (content) => {
    // Check if this is an initialization call with the same content
    // This prevents unnecessary state updates when first loading
    // console.log("hiiHandleEditorChange:",content)
    // console.log("formDataContent:", formData.content)
    if (content === formData.content) {
      console.log('Skipping redundant editor change event');
      return;
    }

    const text = content.replace(/<[^>]*>/g, '');
    const wordCount = text
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
    const readTimeMinutes = Math.ceil(wordCount / 200);

    setFormData((prevFormData) => ({
      ...prevFormData, // Keep all previous form data
      content, // Update content
      read_time: prevFormData.read_time || `${readTimeMinutes} min read` // Only update read_time if it's empty
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          image_url: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };
  const handleAuthorImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setAuthorImageFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          author_image_url: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (
      !formData.title ||
      !formData.content ||
      !formData.slug ||
      !formData.category_name
    ) {
      alert(
        'Please fill in required fields: Title, URL Slug, Category, and Content'
      );
      return;
    }

    if (!formData.read_time) {
      const text = formData.content?.replace(/<[^>]*>/g, '') || '';
      const wordCount = text
        .split(/\s+/)
        .filter((word) => word.length > 0).length;
      formData.read_time = `${Math.ceil(wordCount / 200)} min read`;
    }

    setIsSubmitting(true);
    setError('');

    const isEdit = !!blogToEdit;

    const updatedFormData = {
      ...formData,
      updated_by: loggedInUser?.fullName || 'Admin'
    };

    if (!isEdit) {
      updatedFormData.created_by = loggedInUser?.fullName || 'Admin';
      if (!updatedFormData.author_profile_link && loggedInUser?.email) {
        updatedFormData.author_profile_link = `/authors/${
          loggedInUser.email.split('@')[0]
        }`;
      }
    }

    try {
      if (imageFile) {
        const imageResponse = await uploadBlogImage(imageFile);
        updatedFormData.image_url = imageResponse.imageUrl;
      }
      if (authorImageFile) {
        const imageResponse = await uploadBlogImage(authorImageFile);
        updatedFormData.author_image_url = imageResponse.imageUrl;
      }

      let response;
      if (isEdit && updatedFormData.slug) {
        let updatedId = updatedFormData.id;
        delete updatedFormData.id;
        delete updatedFormData.blogCategoryId;
        delete updatedFormData.authorId;
        delete updatedFormData.publishedAt;
        delete updatedFormData.createdAt;
        delete updatedFormData.updatedAt;
        delete updatedFormData.publishedBy;
        delete updatedFormData.deletedAt;
        delete updatedFormData.author;
        delete updatedFormData.category;
        response = await updateBlog(updatedId, updatedFormData);
      } else {
        response = await createBlog(updatedFormData);
      }

      onSave(response, isEdit);
      handleClose();
    } catch (err) {
      setError(
        err.response?.data?.message + err.response?.data?.validation?.body?.message || 'Failed to save blog. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContentEditor = () => {
    return (
      <Form.Group className="mb-3">
        <Form.Label>Content</Form.Label>
        <div className="d-flex justify-content-end mb-1">
          <QuoteToolbar quill={quillRef.current?.getEditor()} />
        </div>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={formData.content || ''}
          onChange={handleEditorChange}
          style={{ height: 'fit-content', marginBottom: '50px' }}
          modules={{
            toolbar: [
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              ['blockquote', 'code-block'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ color: [] }, { background: [] }],
              ['link', 'image'],
              ['clean']
            ]
          }}
        />
        <div className="text-muted mt-2 small">
          <strong>Tip:</strong> Use the <strong>Quote</strong> button above for
          quotes with attribution, or select text and click the{' '}
          <span className="text-primary">"</span> button in the toolbar for
          simple quotes.
          <br />
          Example:{' '}
          <span className="fst-italic">
            "Good content tells a story. Great content tells your story."
          </span>
        </div>
      </Form.Group>
    );
  };
  useEffect(() => {
    // console.log('FormData updated:', formData);
  }, [formData]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      // onExited={onExited}
      onExited={() => {
        initializedRef.current = false;
        onExited && onExited();
      }}
      className="custom-modal"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {blogToEdit ? 'Edit Blog' : 'Create New Blog'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger mb-3">{error}</div>}
        <div className="mb-4" style={{ display: 'flex', gap: '2px' }}>
          <Button
            variant={activeTab === 'content' ? 'primary' : 'outline-primary'}
            className="me-2"
            onClick={() => setActiveTab('content')}
          >
            Content
          </Button>
          <Button
            variant={activeTab === 'seo' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('seo')}
          >
            SEO
          </Button>
        </div>

        {activeTab === 'content' ? (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Blog Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Slug</Form.Label>
              <Form.Control
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
              />
              <Form.Text className="text-muted">
                URL: /blog/{formData.slug}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                name="category_name"
                value={formData.category_name}
                onChange={handleChange}
                disabled={loadingCategories}
              >
                <option value="">Select Category</option>
                {loadingCategories ? (
                  <option disabled>Loading categories...</option>
                ) : (
                  categories.map((category, index) => (
                    <option key={index} value={category}>
                      {category}
                    </option>
                  ))
                )}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Featured Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {formData.image_url && (
                <div className="mt-2">
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    style={{ maxHeight: '200px', maxWidth: '100%' }}
                  />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Author Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleAuthorImageChange}
              />
              {formData.author_image_url && (
                <div className="mt-2">
                  <img
                    src={formData.author_image_url}
                    alt="Preview"
                    style={{ maxHeight: '200px', maxWidth: '100%' }}
                  />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Author Profile Link</Form.Label>
              <Form.Control
                type="text"
                name="author_profile_link"
                value={formData.author_profile_link}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Author Bio</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="author_bio"
                value={formData.author_bio}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Estimated Read Time</Form.Label>
              <Form.Control
                type="text"
                name="read_time"
                value={formData.read_time}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>TL;DR Summary</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="tldr_summary"
                value={formData.tldr_summary}
                onChange={handleChange}
              />
            </Form.Group>

            {renderContentEditor()}
          </>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Meta Title</Form.Label>
              <Form.Control
                type="text"
                name="meta_title"
                value={formData.meta_title}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Meta Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="meta_description"
                value={formData.meta_description}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Keywords</Form.Label>
              <Form.Control
                type="text"
                name="keywords"
                value={formData.keywords}
                onChange={handleChange}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {blogToEdit ? 'Updating...' : 'Creating...'}
            </>
          ) : blogToEdit ? (
            'Update'
          ) : (
            'Create'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BlogEditor;
